<script setup lang="ts">
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { cloneDeep } from 'lodash-es';
import { NotificationActionOptions, NotificationTypeRecord } from '@/constants/business';
import { findPage, readNotification } from '@/service/api/notify/notification-account';
import { useNotifyStore } from '@/store/modules/notify';
import { usePageFetch } from '@/hooks/common/use-page-fetch';
import { addFilter } from '@/utils/common';
import { NotificationTypeEnum } from '@/enum';
import type { Api } from '@/typings/api';

defineOptions({
  name: 'NotificationDrawer'
});

const router = useRouter();
const notifyStore = useNotifyStore();
const visible = defineModel('visible', {
  type: Boolean,
  default: false
});

const activeTab = ref(NotificationActionOptions[0].value);
const searchValue = ref('');
const searchInputWidth = ref(85);
const showRead = ref(false);

const { loading, page, pageData, refresh, getNextPage } = usePageFetch({
  fetchFun: params => {
    const searchParam = cloneDeep(params);
    addFilter(searchParam.filter, {
      column: 'notification.action',
      type: 'andEq',
      value: activeTab.value
    });
    if (searchValue.value) {
      addFilter(searchParam.filter, {
        column: null,
        type: 'andOr',
        value: [
          {
            column: 'notification.title',
            type: 'andLike',
            value: `%${searchValue.value}%`
          },
          {
            column: 'notification.content',
            type: 'andLike',
            value: `%${searchValue.value}%`
          }
        ]
      });
    }
    if (!showRead.value) {
      addFilter(searchParam.filter, {
        column: 'isRead',
        type: 'andEq',
        value: false
      });
    }
    return findPage(searchParam);
  },
  column: [
    'id',
    'isRead',
    'readTime',
    'createTime',
    'notification.id',
    'notification.title',
    'notification.content',
    'notification.action',
    'notification.type',
    'notification.objectId'
  ],
  filter: []
});

watch(
  activeTab,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      refresh();
    }
  },
  { immediate: true }
);

watch(
  showRead,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      refresh();
    }
  },
  { immediate: true }
);

watch(
  () => notifyStore.lastReadNotificationId,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      if (pageData.value?.length) {
        for (const row of pageData.value) {
          if (row.id === notifyStore.lastReadNotificationId) {
            row.isRead = true;
          }
        }
      }
    }
  }
);

async function handleClick(row: Api.Notify.NotificationAccount) {
  switch (row.notification.type) {
    case NotificationTypeEnum.SYSTEM:
      break;
    case NotificationTypeEnum.SALE_ORDER:
      router.push({
        path: '/store-store/store-sale-order'
      });
      break;
    case NotificationTypeEnum.FILM_PURCHASE_ORDER:
      router.push({
        path: '/store-inventory/film-purchase-order'
      });
      break;
    case NotificationTypeEnum.STORE_PURCHASE_ORDER:
      router.push({
        path: '/store-inventory/purchase-order'
      });
      break;
    case NotificationTypeEnum.STORE_CUSTOMER_TRANSFER:
      router.push({
        path: '/store-customer-service/member-circulation-info'
      });
      break;
    case NotificationTypeEnum.STORE_APPOINTMENT:
      router.push({
        path: '/store-customer/appointment-info'
      });
      break;
    case NotificationTypeEnum.PRODUCT_INVENTORY:
      router.push({
        path: '/store-inventory/inventory-info'
      });
      break;
    case NotificationTypeEnum.FILM_INVENTORY:
      router.push({
        path: '/store-inventory/store-inventory-info'
      });
      break;

    default:
      break;
  }
  await read(row);
}

function read(row: Api.Notify.NotificationAccount) {
  if (row.isRead) {
    return;
  }
  row.isRead = true;
  readNotification(row.id).catch(() => {
    row.isRead = false;
  });

  // Refresh unread count after reading a notification
  // This is a temporary update - the SSE connection will provide the accurate count
  const currentCount = notifyStore.unreadCount;
  if (currentCount > 0) {
    notifyStore.readNotify(activeTab.value);
  }
}
</script>

<template>
  <NDrawer
    v-model:show="visible"
    display-directive="show"
    :default-width="380"
    class="max-w-80% min-w-[380px]"
    resizable
  >
    <NDrawerContent title="消息中心" :native-scrollbar="false" closable>
      <NTabs v-model:value="activeTab">
        <NTab v-for="item of NotificationActionOptions" :key="item.value" :name="item.value">
          {{ `${item.label} (${notifyStore.unreadAction[item.value]})` }}
        </NTab>
        <template #suffix>
          <NInputGroup>
            <NInput
              v-model:value="searchValue"
              :style="{ width: searchInputWidth + 'px' }"
              size="small"
              round
              placeholder="输入关键词"
              clearable
              maxlength="50"
              class="transition-width"
              @keyup.enter="refresh"
              @focus="() => (searchInputWidth = 200)"
              @blur="() => (searchInputWidth = 85)"
            >
              <template #prefix>
                <icon-ant-design-search-outlined />
              </template>
            </NInput>
            <NButton type="primary" size="small" round>搜索</NButton>
          </NInputGroup>
        </template>
      </NTabs>
      <NInfiniteScroll class="h-[calc(100vh-190px)]" :distance="10" @load="getNextPage">
        <NList>
          <NListItem v-for="row of pageData" :key="row.id" @click="read(row)">
            <NThing
              :content="row.notification.content"
              content-style="margin-top: 10px;white-space: pre-line;"
              description-class="flex items-center"
            >
              <template #header>
                <NText :delete="row.isRead" :depth="row.isRead ? 3 : 1">
                  {{ row.notification.title }}
                </NText>
              </template>
              <template #header-extra>
                <NButton text quaternary type="primary" size="small" @click="handleClick(row)">
                  查看
                  <icon-material-symbols-arrow-forward-ios-rounded />
                </NButton>
              </template>
              <template #description>
                <NTag
                  :bordered="false"
                  size="small"
                  class="mr-10px"
                  :color="{
                    color: NotificationTypeRecord[row.notification.type as NotificationTypeEnum].config?.color,
                    textColor: '#fff'
                  }"
                >
                  {{ NotificationTypeRecord[row.notification.type as NotificationTypeEnum].label }}
                </NTag>
                <span class="text-gray-600">{{ row.createTime }}</span>
              </template>
            </NThing>
          </NListItem>
          <NListItem v-if="loading" class="text-center">加载中...</NListItem>
          <NListItem v-else-if="page.last" class="text-center">没有更多了</NListItem>
        </NList>
      </NInfiniteScroll>
      <template #footer>
        <div class="flex items-center">
          <span class="mr-8px">显示已读消息</span>
          <NSwitch v-model:value="showRead" size="small"></NSwitch>
        </div>
      </template>
    </NDrawerContent>
  </NDrawer>
</template>

<style scoped></style>
